import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaAngleRight, FaCheckCircle } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import { Badge, Button, Modal, Nav, Table } from "react-bootstrap"
import { apiURL, filesURL } from 'config/globals'
import Topbar from 'components/Topbar'
import ModelViewer2d from "components/ModelViewer2d"
import ModelViewer3d from "components/ModelViewer3d"
import { checkAuth } from 'utils/Auth'
import StickyFooter from "components/StickyFooter"
import SaveButton from "components/SaveButton"
import FormRow from "components/FormRow"
import moment from "moment"

const categoryOptions = [
    { id: 'negativeFeatures', label: 'Negative features' },
    { id: 'loadBearing', label: 'Load bearing elements' },
    { id: 'nonLoadBearing', label: 'Non-load bearing elements' },
    { id: 'frameAndRoof', label: 'Frame and roof substructure' },
    { id: 'roof', label: 'Roof' },
    { id: 'doorsAndWindows', label: 'Doors and windows' },
]

const BuildingRecordNew = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [building, setBuilding] = useState({})
    const [documentationNumber, setDocumentationNumber] = useState("")
    const [documentationDate, setDocumentationDate] = useState("")
    const [currentCategory, setCurrentCategory] = useState(categoryOptions[0].id)
    const [currentRecord, setCurrentRecord] = useState({markers: []})
    const [loadError, setLoadError] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    const navigate = useNavigate()
    const { entityId, buildingId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            await getBuilding(buildingId)
            await getNewDocumentationNumber(buildingId)
            setDocumentationDate(moment(new Date()).format('YYYY-MM-DD'))
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getBuilding = async (id) => {
        const response = await axios.get(`${apiURL}/get-building/${id}`)

        if (!response.data.success) {
            setLoadError("Cannot load building data: " + (response.data.error || response.data))
            return
        }

        setBuilding(response.data.item)
    }
    
    const getNewDocumentationNumber = async (buildingId) => {
        const response = await axios.get(`${apiURL}/get-new-documentation-number/${buildingId}`)

        if (!response.data.success) {
            setLoadError("Cannot load the building documentation number: " + (response.data.error || response.data))
            return
        }

        setDocumentationNumber(response.data.number)
    }

    const addMarker = (marker) => {
        let record = {...currentRecord}
        record.markers.push(marker)
        setCurrentRecord(record)
    }

    const updateMarker = (marker) => {
        let record = {...currentRecord}
        const existingMarker = record.markers.find(x => x.id === marker.id)
        const index = record.markers.indexOf(existingMarker)
        record.markers.splice(index, 1, marker)
        setCurrentRecord(record)
    }

    const selectCategory = (categoryId) => {
        setCurrentCategory(categoryId)
    }

    const saveDraft = async () => {
        if (await save('draft')) {
            setSuccessMessage("The record has been saved successfully.")
        }
    }

    const saveAndSubmit = async () => {
        if (await save('review')) {
            setSuccessMessage("The record has been successfully saved and submitted for review.")
        }
    }

    const save = async (status) => {
        const record = {...currentRecord}
        record.buildingId = buildingId
        if (!record.createdBy) {
            record.createdBy = currentUser.email
        }
        record.modifiedBy = currentUser.email
        record.status = status

        setIsSaving(true)

        try {
            const response = record.id
                ? await axios.put(`${apiURL}/update-record/${record.id}`, record)
                : await axios.post(`${apiURL}/create-record`, record)

            setIsSaving(false)
            
            if (!response.data.success) {
                setErrorMessage(response.data.error || response.data)
                return
            }

            if (!record.id) {
                record.id = response.data.id
            }
            setCurrentRecord(record)
            return true
        } catch (error) {
            setErrorMessage(JSON.stringify(error))
            setIsSaving(false)
        }
    }

    const previousCategory = () => {
        const categoryIndex = categoryOptions.findIndex(x => x.id === currentCategory)
        selectCategory(categoryOptions[categoryIndex - 1].id)
    }

    const nextCategory = () => {
        const categoryIndex = categoryOptions.findIndex(x => x.id === currentCategory)
        selectCategory(categoryOptions[categoryIndex + 1].id)
    }

    const navigateToBuilding = () => {
        navigate(`/museum/${entityId}/documentation/buildings/${building.id}/view`)
    }

    const navigateToEditRecord = () => {
        navigate(`/museum/${entityId}/documentation/buildings/${building.id}/edit-record/${currentRecord.id}`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width">
                {building.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation/buildings/${building.id}/view`} className="no-decoration">
                                <FaAngleLeft /> Back to Building
                            </Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>{building.name}</h1>
                            </div>
                        </header>
                        <div className="mb-4">
                            <FormRow
                                key="documentationNumber"
                                label="Documentation Number"
                                content={documentationNumber}
                                noMargin
                            />
                            <FormRow
                                key="documentationdate"
                                label="Documentation Date"
                                content={documentationDate}
                                noMargin
                            />
                            <FormRow
                                key="inventoryNumber"
                                label="Inventory Number"
                                content={building.inventoryNumber ?? "--"}
                                noMargin
                            />
                            <FormRow
                                key="constructionDate"
                                label="Construction date"
                                content={building.constructionDate ?? "--"}
                                noMargin
                            />
                            <FormRow
                                key="buildingPurpose"
                                label="Purpose of building"
                                content={building.buildingPurpose ?? "--"}
                                noMargin
                            />
                        </div>
                        <Nav variant="pills" defaultActiveKey="negativeFeatures" className="mb-4" onSelect={selectCategory}>
                            <Nav.Item>
                                <Nav.Link eventKey="negativeFeatures" active={currentCategory === "negativeFeatures"}>Negative features</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="loadBearing" active={currentCategory === "loadBearing"}>Load bearing elements</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="nonLoadBearing" active={currentCategory === "nonLoadBearing"}>Non-load bearing elements</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="frameAndRoof" active={currentCategory === "frameAndRoof"}>Frame and roof substructure</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="roof" active={currentCategory === "roof"}>Roof</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="doorsAndWindows" active={currentCategory === "doorsAndWindows"}>Doors and windows</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        {building.modelType === '3d' &&
                            <ModelViewer3d
                                modelFileName={building.modelFile}
                                record={currentRecord}
                                allowNewMarkers={true}
                                currentCategory={currentCategory}
                                onAddMarker={addMarker}
                                onUpdateMarker={updateMarker}
                            />
                        }
                        {building.modelType === '2d' &&
                            <ModelViewer2d
                                modelFileName={building.modelFile}
                                record={currentRecord}
                                allowNewMarkers={true}
                                currentCategory={currentCategory}
                                onAddMarker={addMarker}
                                onUpdateMarker={updateMarker}
                            />
                        }
                        <div>
                            {currentRecord && currentRecord.markers.length
                                ? <Table striped bordered hover cellPadding="15" style={{marginBottom: 200}}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Title</th>
                                            <th>Description</th>
                                            <th>Category</th>
                                            <th>Type</th>
                                            <th>Image</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRecord.markers
                                            .map(marker => (
                                                <tr key={marker.id} className="recordRow">
                                                    <td>
                                                        {marker.id}
                                                    </td>
                                                    <td>
                                                        {marker.title}
                                                    </td>
                                                    <td>
                                                        {marker.description}
                                                    </td>
                                                    <td>
                                                        {categoryOptions.find(x => x.id === marker.category)?.label}
                                                    </td>
                                                    <td>
                                                        {marker.type === "condition" &&
                                                            <Badge bg="warning" text="dark">
                                                                Condition
                                                            </Badge>
                                                        }
                                                        {marker.type === "damage" &&
                                                            <Badge bg="danger">
                                                                Damage
                                                            </Badge>
                                                        }
                                                    </td>
                                                    <td>
                                                        {marker.image &&
                                                            <img src={`${filesURL}/${marker.image}`} style={{width: '100%', maxWidth: 300}} />
                                                        }
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                  </Table>
                                : <>
                                    No marks added.
                                  </>
                            }
                        </div>
                    </>
                }
                {loadError &&
                    <Alert key='loadError' variant='danger'>
                        {loadError}
                    </Alert>
                }
                {errorMessage &&
                    <Modal show={!!errorMessage} onHide={() => setErrorMessage("")} backdrop="static" centered>
                        <Modal.Header closeButton>
                            <Modal.Title className="text-danger">
                                Error
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {errorMessage}
                        </Modal.Body>
                    </Modal>
                }
                {successMessage &&
                    <Modal show={!!successMessage} onHide={() => setSuccessMessage("")} backdrop="static" centered>
                        <Modal.Header>
                            <Modal.Title className="text-success">
                                <FaCheckCircle /> Success
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {successMessage}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="outline-primary" onClick={navigateToEditRecord}>
                                Keep editing
                            </Button>
                            <Button variant="primary" onClick={navigateToBuilding}>
                                View building
                            </Button>
                        </Modal.Footer>
                    </Modal>
                }
                <StickyFooter>
                    {categoryOptions.findIndex(x => x.id === currentCategory) > 0 &&
                        <Button size="lg" variant="outline-primary" onClick={previousCategory}>
                            <FaAngleLeft /> Back
                        </Button>
                    }
                    {categoryOptions.findIndex(x => x.id === currentCategory) < categoryOptions.length - 1
                        ? <Button size="lg" variant="primary" className="float-end" onClick={nextCategory}>
                            Next <FaAngleRight />
                          </Button>
                        : <SaveButton isSubmitting={isSaving} title="Save and submit for review" className="float-end" onClick={saveAndSubmit} />
                    }
                    <SaveButton variant="outline-primary" hideIcon isSubmitting={isSaving} title="Save draft" className="float-end me-3" onClick={saveDraft} />
                </StickyFooter>
            </main>
        </>
    )
}

export default BuildingRecordNew